/**
 * Formata texto com marcadores do WhatsApp para exibição em HTML.
 * Regras: *negrito* | _itálico_ | ~tachado~ | ```monoespaço```
 * Por padrão o texto é escapado para evitar XSS, mas preservamos HTML
 * legado vindo do banco (ex.: <span style="color:red">...</span>).
 */

const escapeHtml = (str) => {
  if (str == null || typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

// Remove scripts e handlers perigosos de um HTML legado simples.
const sanitizeLegacyHtml = (html) => {
  if (html == null || typeof html !== "string") return "";

  let cleaned = html;

  // Remove qualquer bloco <script>...</script>
  cleaned = cleaned.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");

  // Remove atributos on*="..." (onclick, onload, etc.)
  cleaned = cleaned.replace(/\s+on\w+="[^"]*"/gi, "");
  cleaned = cleaned.replace(/\s+on\w+='[^']*'/gi, "");

  // Remove javascript: em href/src
  cleaned = cleaned.replace(/javascript:/gi, "");

  return cleaned;
};

/**
 * Converte marcadores de formatação do WhatsApp em HTML seguro.
 * Ordem: monospace (```), itálico (_), negrito (*), tachado (~).
 *
 * - Quando o texto contém tags HTML reais (<span>, <p>, <a>, etc.),
 *   assume-se que é HTML legado do sistema antigo e ele é preservado
 *   (apenas sanitizado para remover scripts/handlers perigosos).
 * - Quando não há HTML, o texto é escapado e apenas os marcadores
 *   de WhatsApp são convertidos em tags.
 *
 * @param {string} text - Texto da mensagem (pode conter * _ ~ ``` ou HTML legado)
 * @returns {string} HTML seguro para uso em dangerouslySetInnerHTML
 */
export function formatWhatsAppText(text) {
  if (text == null || typeof text !== "string") return "";

  const raw = String(text);

  // Detecta se parece conter HTML "real" (ex.: <span ...>, <p>, <a href="...">)
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);

  if (hasHtmlTags) {
    // HTML legado: preserva estrutura, apenas sanitiza riscos óbvios.
    return sanitizeLegacyHtml(raw);
  }

  // Texto simples: escapa e aplica regras de formatação do WhatsApp.
  const escaped = escapeHtml(raw);

  return escaped
    .replace(/```([\s\S]*?)```/g, "<code>$1</code>")
    .replace(/_([\s\S]*?)_/g, "<em>$1</em>")
    .replace(/\*([\s\S]*?)\*/g, "<strong>$1</strong>")
    .replace(/~([\s\S]*?)~/g, "<s>$1</s>");
}
